/**
 * The Environments this device paired with by link, as the catalog sees them.
 *
 * The flow follows T3 Code's remote pairing: the machine being reached mints a one-time grant and
 * hands it over as a link; this device redeems it for a session at the address the link names and
 * keeps that session, reconnecting with it until the machine revokes it. Saved per device in
 * Client Settings, beside the SSH destinations.
 */
import { PairedConnectionTarget } from "@afar-research/client-runtime/connection";
import type { PlatformConnectionChange } from "@afar-research/client-runtime/platform";
import {
  ENVIRONMENT_ACCESS_EXCHANGE_PATH,
  EnvironmentAccessExchangeResult,
  EnvironmentId,
  ExecutionEnvironmentDescriptor,
  type PairedConnectionSetting,
} from "@afar-research/contracts";
import { resolveRemotePairingTarget } from "@afar-research/shared/remotePairing";
import * as Schema from "effect/Schema";

import { getClientSettingsSnapshot, persistClientSettingsUpdate } from "../clientSettingsStore";
import {
  isEnabledOnThisDevice,
  savedConnectionChanges,
  subscribeSavedSetting,
} from "./savedConnections";

const ENVIRONMENT_DESCRIPTOR_PATH = "/.well-known/afar-research/environment";
const decodeExchange = Schema.decodeUnknownSync(EnvironmentAccessExchangeResult);
const decodeDescriptor = Schema.decodeUnknownSync(ExecutionEnvironmentDescriptor);

export function readPairedConnections(): ReadonlyArray<PairedConnectionSetting> {
  return getClientSettingsSnapshot().pairedConnections;
}

export function pairedConnectionRegistration(connection: PairedConnectionSetting) {
  return {
    enabled: isEnabledOnThisDevice(connection.environmentId),
    target: new PairedConnectionTarget({
      environmentId: connection.environmentId,
      label: connection.label,
      httpBaseUrl: connection.httpBaseUrl,
      wsBaseUrl: connection.wsBaseUrl,
      session: connection.session,
    }),
  };
}

export function pairedEnvironmentConnectionChanges(input: {
  readonly saved: ReadonlyArray<PairedConnectionSetting>;
  readonly published: ReadonlyArray<EnvironmentId>;
}): {
  readonly changes: ReadonlyArray<PlatformConnectionChange>;
  readonly published: ReadonlyArray<EnvironmentId>;
} {
  return savedConnectionChanges({
    registrations: input.saved.map(pairedConnectionRegistration),
    published: input.published,
  });
}

/** Calls `listener` whenever the paired set may have changed; see `subscribeSavedSetting`. */
export function subscribePairedConnections(listener: () => void): () => void {
  return subscribeSavedSetting(readPairedConnections, listener);
}

/** Keyed by the Environment: pairing the same machine again replaces the session it held. */
export async function savePairedConnection(connection: PairedConnectionSetting): Promise<void> {
  await persistClientSettingsUpdate((settings) => ({
    ...settings,
    pairedConnections: [
      ...settings.pairedConnections.filter(
        (entry) => entry.environmentId !== connection.environmentId,
      ),
      connection,
    ],
  }));
}

export async function forgetPairedConnection(environmentId: EnvironmentId): Promise<void> {
  await persistClientSettingsUpdate((settings) => ({
    ...settings,
    pairedConnections: settings.pairedConnections.filter(
      (entry) => entry.environmentId !== environmentId,
    ),
  }));
}

const describeFailure = async (response: Response, what: string): Promise<Error> => {
  const detail = await response.text().catch(() => "");
  return new Error(
    `${what} (HTTP ${response.status})${detail.includes("expired") ? ": the link has expired" : detail.includes("already-redeemed") ? ": the link was already used" : "."}`,
  );
};

/**
 * Pairs this device with the machine a link or an address and code names, and saves it.
 *
 * The grant is redeemed first, because it is spent either way and the session is what everything
 * after needs; the descriptor is then read with that session, which is also the proof that the
 * machine answering is the one that issued it. `fetchFn` is the renderer's own `fetch` — the
 * machine's Environment Access routes answer this client's origin.
 */
export async function pairWithMachine(
  input: { readonly pairingUrl?: string; readonly host?: string; readonly pairingCode?: string },
  options: { readonly clientLabel: string; readonly fetchFn?: typeof globalThis.fetch },
): Promise<PairedConnectionSetting> {
  const fetchFn = options.fetchFn ?? globalThis.fetch;
  const target = resolveRemotePairingTarget(input);
  let exchangeResponse: Response;
  try {
    exchangeResponse = await fetchFn(
      new URL(ENVIRONMENT_ACCESS_EXCHANGE_PATH, target.httpBaseUrl),
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ grant: target.credential, clientLabel: options.clientLabel }),
      },
    );
  } catch {
    throw new Error(`Could not reach ${new URL(target.httpBaseUrl).host}.`);
  }
  if (!exchangeResponse.ok) {
    throw await describeFailure(exchangeResponse, "The machine refused the pairing code");
  }
  const exchanged = decodeExchange(await exchangeResponse.json());
  const descriptorResponse = await fetchFn(
    new URL(ENVIRONMENT_DESCRIPTOR_PATH, target.httpBaseUrl),
    { headers: { authorization: `Bearer ${exchanged.session}` } },
  );
  if (!descriptorResponse.ok) {
    throw await describeFailure(descriptorResponse, "The machine did not describe itself");
  }
  const descriptor = decodeDescriptor(await descriptorResponse.json());
  const connection: PairedConnectionSetting = {
    environmentId: EnvironmentId.make(exchanged.environmentId),
    label: descriptor.label,
    httpBaseUrl: target.httpBaseUrl,
    wsBaseUrl: target.wsBaseUrl,
    session: exchanged.session,
  };
  await savePairedConnection(connection);
  return connection;
}
